// src/components/dashboard/AddressPromptModal.jsx
//
// Shown after login when the seller has not submitted a business address
// (company_address empty OR has_submitted_address = 0). Uses the same
// LocationPicker as the complete-profile location step; saving updates
// user_accounts.company_address and sets has_submitted_address = 1.
import React, { useState, useEffect } from 'react';
import { showToast } from '../../utils/helpers';
import api from '../../utils/api';
import { API_CONFIG } from '../../utils/apiConfig';
import { useAuth } from '../../contexts/AuthContext';
import LocationPicker from '../LocationPicker';

const AddressPromptModal = ({ show, onClose, initialQuery = '', onSaved }) => {
  const { refreshUserData } = useAuth();
  const [location, setLocation] = useState(null); // { lat, lon, name }
  const [saving, setSaving] = useState(false);

  // Start fresh each time the modal opens.
  useEffect(() => {
    if (show) setLocation(null);
  }, [show]);

  // "Maybe later" dismisses the popup for the rest of this browser session,
  // so it doesn't reappear on every page navigation.
  const handleLater = () => {
    sessionStorage.setItem('paymeo_address_prompt_dismissed', '1');
    setLocation(null);
    onClose();
  };

  const reset = () => setLocation(null);

  const handleSave = async () => {
    if (!location) {
      showToast('Please pick your business address to continue', 'error');
      return;
    }
    setSaving(true);
    try {
      const response = await api.post(API_CONFIG.USER.UPDATE_COMPANY_ADDRESS, {
        address: location.name,
        latitude: location.lat,
        longitude: location.lon,
      });
      const data = response.data;
      if (data.error) {
        showToast(data.error, 'error');
        return;
      }
      await refreshUserData();
      showToast('Address saved successfully', 'success');
      if (onSaved) onSaved(location.name);
      onClose();
    } catch (error) {
      console.error('Error saving address:', error);
      showToast(error.response?.data?.error || 'Failed to save your address. Please try again.', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[70]">
      <div className="bg-white rounded-lg max-w-xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-start justify-between mb-4">
            <div>
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-3">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Where is your business located?</h3>
              <p className="text-sm text-gray-500 mt-1">
                Tell buyers where you sell from — pick your address from the suggestions, use your
                current location, or click on the map.
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 p-1"
              aria-label="Close"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <LocationPicker initialQuery={initialQuery} onSelect={setLocation} />

          <div className="flex space-x-3 pt-5">
            <button
              type="button"
              onClick={handleLater}
              className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
            >
              Maybe later
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={!location || saving}
              className="flex-1 bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Saving...
                </div>
              ) : (
                'Save Address'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddressPromptModal;
